import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Currency,
  DateTime,
  DeleteConfirmation,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import ServiceItemForm from './ServiceItemForm';
import SettingsActionsBanner from '../../SettingsActionsBanner';

const InvoicedIcon = styled(Icon)`
  color: ${colors.primary};
`;

export default function AdditionalServices({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).serviceItems().get();
    setData(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (serviceItem = null) => {
    setDialog(
      <ServiceItemForm
        currency={project.currency}
        projectId={project.id}
        serviceItemId={serviceItem?.id}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (serviceItem) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Additional Service"
        resolve={async (result) => {
          if (result) {
            await api.www.workspaces(workspace.id).projectSettings(project.id).serviceItems(serviceItem.id).delete();
            toast.success('Additional Service deleted successfully.');
            await fetchData();
            selection.deselect(serviceItem.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this additional service?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Additional Services</Page.Title>
        <TitleTooltip message="Schedule the billing of additional services. As an example, these could include fixed fee services deliverables that are billed on a Time & Materials project." />

        {project.permissions.edit && (
          <Page.Actions>
            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="service_items_bulk_checkbox"
              selection={selection}
              isVisible={project.permissions.edit}
              disabled={!data.length}
            />
            <ListView.Column width="8rem">Date</ListView.Column>
            <ListView.Column minWidth="10rem">Invoice Item</ListView.Column>
            <ListView.Column minWidth="20rem">Notes</ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListView.Column width="3rem" />
            <ListViewActions.Column isVisible={project.permissions.edit} />
          </ListView.Header>

          <ListView.Body>
            {data.map((serviceItem) => {
              const isInvoiced = serviceItem.invoice && serviceItem.invoice.statusId !== 'draft';
              const areActionsDisabled = isInvoiced;
              const tooltip = isInvoiced ? "Invoiced additional services can't be modified." : undefined;

              return (
                <ListView.Row
                  key={serviceItem.id}
                  onClick={
                    !areActionsDisabled && project.permissions.edit ? () => handleShowForm(serviceItem) : undefined
                  }>
                  <ListViewSelection.Cell id={serviceItem.id} selection={selection} testId="checkbox_service_item" />
                  <ListView.Cell>
                    <DateTime value={serviceItem.date} />
                  </ListView.Cell>
                  <ListView.Cell>{serviceItem.invoiceItem.name}</ListView.Cell>
                  <ListView.Cell>{serviceItem.description}</ListView.Cell>
                  <ListView.Cell>
                    <Currency value={serviceItem.fee} currency={serviceItem.project.currency} />
                  </ListView.Cell>
                  <ListView.Cell>
                    {isInvoiced && (
                      <Tooltip message="An invoice has been published for this additional service.">
                        <InvoicedIcon icon="file-invoice-dollar" />
                      </Tooltip>
                    )}
                  </ListView.Cell>
                  <ListViewActions isVisible={project.permissions.edit}>
                    <ListViewActions.Edit
                      disabled={areActionsDisabled}
                      tooltip={tooltip}
                      onClick={() => handleShowForm(serviceItem)}
                    />
                    <hr />
                    <ListViewMenu>
                      <ListViewMenu.Item disabled={areActionsDisabled} onClick={() => handleShowForm(serviceItem)}>
                        Edit
                      </ListViewMenu.Item>

                      <ListViewMenu.DeleteItem
                        disabled={areActionsDisabled}
                        tooltip={tooltip}
                        onClick={() => handleDelete(serviceItem)}>
                        Delete
                      </ListViewMenu.DeleteItem>
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length > 0 && (
              <ListView.TotalRow>
                <ListView.Cell />
                <ListView.Cell>Total</ListView.Cell>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <Currency value={_.sumBy(data, 'fee')} currency={project.currency} />
                </ListView.Cell>
                <ListView.Cell />
                <ListViewActions.Cell isVisible={project.permissions.edit} />
              </ListView.TotalRow>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Additional Service" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
